import type { Cuisine, Recipe, SystemTags } from '@hewey/shared'
import { cleanCuisine } from './tagSummary.ts'
import type { RecipeFilter } from './filter.ts'
import { totalMins } from './time.ts'

// A suggested-collection row for the Explore landing. `seeAll`, when present,
// is the filter patch that reproduces the row in the results grid — tapping
// "See all" applies it. The recency row has no filter equivalent, so it omits
// it; the "View all" button covers seeing everything.
export type Collection = {
	key: string
	title: string
	subtitle?: string
	recipes: Recipe[]
	seeAll?: Partial<RecipeFilter>
}

// Per-row cap; rows are two-tall horizontal scrollers, so a dozen is plenty
// before "See all" earns its keep.
const CAP = 12

// A cuisine needs at least this many recipes to earn its own row, and we show
// at most this many cuisine rows — the best-stocked ones.
const CUISINE_MIN = 3
const CUISINE_ROWS = 3

type TaggedRecipe = Recipe & { systemTags: SystemTags }

// Only tagged recipes carry the facets the tag-based rows key off. Pending and
// failed recipes still feed the recency row, just not these.
const isTagged = (r: Recipe): r is TaggedRecipe =>
	r.taggingStatus === 'tagged' && r.systemTags !== null

const byCreatedDesc = (a: Recipe, b: Recipe): number =>
	b.createdAt.localeCompare(a.createdAt)

const row = (
	key: string,
	title: string,
	recipes: Recipe[],
	extra: { subtitle?: string; seeAll?: Partial<RecipeFilter> } = {},
): Collection | null =>
	recipes.length === 0
		? null
		: { key, title, recipes: recipes.slice(0, CAP), ...extra }

const recentRow = (recipes: Recipe[]): Collection | null =>
	row('recent', 'Recently added', [...recipes].sort(byCreatedDesc))

// The time rows are dinner rows, so a 40-minute cake doesn't belong in them
// however quick it is. Untagged recipes have no type to judge on, so they stay
// in — same rule as everywhere else here.
const isMealCourse = (r: Recipe): boolean =>
	r.systemTags === null ||
	(r.systemTags.type !== 'dessert' && r.systemTags.type !== 'snack')

// Time-based rows key off the scraped prep+cook minutes, which live on every
// recipe regardless of tagging status — so they run over the full list, not
// just `tagged`. Recipes with no published time are excluded.
const withinMins = (recipes: Recipe[], max: number): Recipe[] =>
	recipes.filter((r) => {
		const m = totalMins(r)
		return m !== null && m <= max && isMealCourse(r)
	})

const quickRow = (recipes: Recipe[]): Collection | null =>
	row('quick', 'Quick dinners', withinMins(recipes, 30).sort(byCreatedDesc), {
		subtitle: '30 minutes or less',
		seeAll: { times: ['quick'] },
	})

const weeknightRow = (recipes: Recipe[]): Collection | null =>
	row(
		'weeknight',
		'Weeknight dinners',
		withinMins(recipes, 45).sort(byCreatedDesc),
		{ subtitle: '45 minutes or less', seeAll: { times: ['weeknight'] } },
	)

const weekendRow = (recipes: Recipe[]): Collection | null =>
	row(
		'weekend',
		'For the weekend',
		recipes
			.filter((r) => {
				const m = totalMins(r)
				return m !== null && m > 45 && isMealCourse(r)
			})
			.sort(byCreatedDesc),
		{ subtitle: 'A bigger cook', seeAll: { times: ['weekend'] } },
	)

// The row desserts do belong in, alongside the meals worth the extra work.
// Effort rather than time is the test: a slow braise is a weeknight-ahead
// meal, while a high-effort dish is the one you cook for other people. No
// `seeAll` — the filter bar has no effort or type facet to reproduce it with.
const hostingRow = (tagged: TaggedRecipe[]): Collection | null =>
	row(
		'hosting',
		'Hosting guests',
		tagged
			.filter(
				(r) =>
					r.systemTags.effort === 'high' ||
					r.systemTags.type === 'dessert',
			)
			.sort(byCreatedDesc),
		{ subtitle: 'Nicer meals and desserts' },
	)

const meatFreeRow = (tagged: TaggedRecipe[]): Collection | null =>
	row(
		'meat-free',
		'Meat-free',
		tagged
			.filter(
				(r) =>
					r.systemTags.primaryProtein === 'none' ||
					r.systemTags.primaryProtein === 'beans_legumes',
			)
			.sort(byCreatedDesc),
		{ seeAll: { proteins: ['none', 'beans_legumes'] } },
	)

// One row per cuisine the family actually cooks, best-stocked first — so the
// landing reflects this household's collection rather than a fixed guess. The
// catch-all "other" cuisine has no clean label, so it never earns a row.
const cuisineRows = (tagged: TaggedRecipe[]): Collection[] => {
	const counts = new Map<Cuisine, number>()
	for (const r of tagged) {
		const c = r.systemTags.cuisine
		if (!cleanCuisine[c]) continue
		counts.set(c, (counts.get(c) ?? 0) + 1)
	}
	return [...counts.entries()]
		.filter(([, n]) => n >= CUISINE_MIN)
		.sort(([, a], [, b]) => b - a)
		.slice(0, CUISINE_ROWS)
		.map(([cuisine]) => ({
			key: `cuisine-${cuisine}`,
			title: cleanCuisine[cuisine] as string,
			recipes: tagged
				.filter((r) => r.systemTags.cuisine === cuisine)
				.sort(byCreatedDesc)
				.slice(0, CAP),
			seeAll: { cuisines: [cuisine] },
		}))
}

// Build the Explore landing rows. Recency leads; the tag-based rows and the
// dynamic per-cuisine rows follow, each dropping out when it has nothing to
// show.
export const buildCollections = (recipes: Recipe[]): Collection[] => {
	const tagged = recipes.filter(isTagged)
	const rows = [
		recentRow(recipes),
		quickRow(recipes),
		weeknightRow(recipes),
		weekendRow(recipes),
		hostingRow(tagged),
		...cuisineRows(tagged),
		meatFreeRow(tagged),
	]
	return rows.filter((c): c is Collection => c !== null)
}
